import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Film,
  Play,
  AlertTriangle,
} from 'lucide-react';
import { Spinner } from '@ntconsult/aluma-react';
import { cn } from '@common/utils/cn';
import { formatFileSize, formatDuration, formatRelativeTime } from '@common/utils/format';
import { ResourceActionMenu } from '@common/components/ResourceActionMenu';
import { SharedBadge } from '@common/components/SharedBadge';
import type { File } from '../types';

interface FileCardProps {
  file: File;
  canWrite?: boolean;
  onPlay?: (file: File) => void;
  onEdit?: (file: File) => void;
  onDelete?: (file: File) => void;
  onDownload?: (file: File) => void;
  onShare?: (file: File) => void;
}

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const buildThumbnailUrl = (fileId: string) =>
  `${API_BASE_URL}/files/${fileId}/thumbnail-proxy`;

export function FileCard({
  file,
  canWrite = true,
  onPlay,
  onEdit,
  onDelete,
  onDownload,
  onShare,
}: FileCardProps) {
  const { t } = useTranslation('files');
  const isProcessing = file.status === 'processing' || file.status === 'confirmed';
  const isFailed = file.status === 'failed';
  const isReady = file.status === 'ready';
  const isInteractive = !isProcessing && !isFailed;

  const thumbnailUrl = useMemo(
    () => (isReady && file.thumbnailKey ? buildThumbnailUrl(file.id) : null),
    [file.id, file.thumbnailKey, isReady],
  );
  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  const handleClick = () => {
    if (isInteractive) {
      onPlay?.(file);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'group bg-surface border border-border rounded-lg overflow-hidden transition-all',
        isProcessing || isFailed
          ? 'cursor-default opacity-90'
          : 'hover:shadow-md hover:border-brand-primary cursor-pointer',
      )}
    >
      <div className="relative aspect-video bg-muted flex items-center justify-center overflow-hidden">
        {thumbnailUrl && !thumbnailFailed ? (
          <img
            src={thumbnailUrl}
            alt={file.name}
            crossOrigin="use-credentials"
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setThumbnailFailed(true)}
          />
        ) : (
          <Film className="w-12 h-12 text-info" aria-hidden="true" />
        )}

        {isProcessing && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 text-white">
            <Spinner size="lg" tone="current" />
            <span className="text-xs font-medium">{t('processing')}</span>
          </div>
        )}

        {isFailed && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
            <AlertTriangle className="w-8 h-8 text-danger" />
            <span className="text-white text-xs font-medium">{t('processingFailed')}</span>
          </div>
        )}

        {!isProcessing && !isFailed && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <div className="w-14 h-14 bg-surface-overlay/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity transform scale-90 group-hover:scale-100">
              <Play className="w-6 h-6 text-foreground ml-1" />
            </div>
          </div>
        )}

        {file.duration && !isProcessing && !isFailed && (
          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
            {formatDuration(file.duration)}
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className={cn(
              'font-medium truncate transition-colors',
              isProcessing
                ? 'text-foreground-muted'
                : isFailed
                  ? 'text-danger'
                  : 'text-foreground group-hover:text-brand-primary',
            )}>
              {file.name}
            </h3>
            <div className="flex items-center gap-2 text-xs text-foreground-muted mt-1">
              <span>{formatFileSize(file.size)}</span>
              <span>•</span>
              <span>{formatRelativeTime(file.createdAt)}</span>
            </div>
            {file.isShared && (
              <div className="mt-1">
                <SharedBadge />
              </div>
            )}
          </div>

          <ResourceActionMenu
            canWrite={canWrite}
            isInteractive={isInteractive}
            onDownload={onDownload ? () => onDownload(file) : undefined}
            onShare={onShare ? () => onShare(file) : undefined}
            onEdit={onEdit ? () => onEdit(file) : undefined}
            onDelete={onDelete ? () => onDelete(file) : undefined}
          />
        </div>
      </div>
    </div>
  );
}
